import { JobStatusResponse } from '@upright/common/internal/jobs/types';
import { getScoreReaderTotal, ScoreReader } from '@upright/common/model/output/scores/score-reader';
import { ApiData } from '@upright/ui/api/common/hooks';
import { defaultNetImpactLensScoreUnit } from '@upright/ui/common/upright/core/impact-lenses/net-impact/selection-state';
import { mapValues } from 'lodash';
import { ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { ImpactLanguage, PossiblyNamedModelEntityReference } from '../../../../../../../../common/model/input/types';
import { ModelEntityWithScores } from '../../../../../../../../common/model/output/scores/model-entity-data/types';
import {
  DEFAULT_RELATIVE_NET_SCORE_FORMAT,
  FinalImpactScoreType,
  ImpactScoresByIVV,
  NetScoreFormat,
} from '../../../../../../../../common/model/output/scores/types';
import { Dimension, Valence, ValueChain } from '../../../../../../../../common/model/types';
import { useMainContext } from '../../../../../../../views/root/main/context';
import { usePrevious } from '../../../../../../generic/hooks';
import { getMultiseriesMaxXFromScoreReaderFunctions } from '../horizontal-scale';
import { allDataHasAbsolute, getHeights, getNumberData, getScaleMode, getWidths } from '../shared';
import { ImpactChartDataSeries, ImpactChartFontSizes, NetImpactChartProps, ScaleMode } from '../types';
import { getImpactScoreFormattersFromScoreReaders, ImpactScoreFormatters } from '../value-formatter';

export interface ImpactChartContext {
  width: number;
  desktopMode: boolean;

  scoreType: FinalImpactScoreType;
  setScoreType: ((mode: FinalImpactScoreType) => void) | undefined;
  relativeMode: boolean;
  absoluteModeDisabled: boolean;

  // OA: Needs to be rethought when we expand test coverage to all model entity view sections
  enableE2eTestIds: true | undefined;

  showLegend: boolean;
  scoreUnitSelectorPlacement: 'left' | 'right';

  showValueChains: boolean;
  setShowValueChains: (mode: boolean) => void;
  hideValueChainsSwitch: boolean;
  showValueChainsSwitch: boolean;
  hideReleaseText: boolean;

  modalImpactCode: string | undefined;
  setModalImpactCode: (code: string | undefined) => void;

  data: ImpactChartDataSeries[];
  relativeData: Array<ImpactScoresByIVV | undefined>;
  absoluteData: Array<ImpactScoresByIVV | undefined>;
  hasFinishedLoading: true | undefined;
  scoreReaders: ScoreReader[];
  /** Per-series reader for the optional ghost baseline
   *  (`ImpactChartDataSeries.ghostScoresByTIVV`); undefined for series
   *  without one. Indexed in lockstep with `scoreReaders`. */
  ghostScoreReaders: Array<ScoreReader | undefined>;

  maxX: number;
  scaleMode: ScaleMode;

  getImpactClickUrl?: (
    valence: Valence,
    valueChain: ValueChain | undefined,
    impactCode: string,
    ref: PossiblyNamedModelEntityReference,
    resultId: number,
  ) => string;
  onImpactClick?: (
    valence: Valence,
    valueChain: ValueChain | undefined,
    impactCode: string,
    ref: PossiblyNamedModelEntityReference,
    resultId: number,
  ) => void;
  disableImpactClick?: boolean;
  onContributeClick?: () => void;

  hideNetScore?: boolean;
  hideTitle?: boolean;
  bigFonts?: boolean;
  title?: string;

  isExpanded: (dimension: Dimension) => boolean;
  setExpanded: (dimension: Dimension, expanded: boolean) => void;

  netScoreFormat: NetScoreFormat;
  formatters: ImpactScoreFormatters;
  singleSeries: boolean;

  staticMode: boolean;

  showRenderViewButton?: boolean;
  alignLineEdges?: boolean;
  entityData?: Array<ModelEntityWithScores | undefined>;
  version?: string;
  release?: string;
  contributingArticlesCount?: number;
  className?: string;
  negativeColor?: string;
  positiveColor?: string;
  language?: ImpactLanguage;
  simplify?: boolean;
  backgroundColor?: string;

  updateJobStatusRequest?: ApiData<JobStatusResponse>;
}

// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
const ctx = createContext<ImpactChartContext>({} as any);
const Provider = ctx.Provider;

interface Props extends NetImpactChartProps {
  width: number;
  children: ReactNode;
}

export function ImpactChartContextProvider(props: Props) {
  const data = props.data;
  const desktopThreshold = props.simplify ? 600 : 650;
  const desktopMode = props.width > desktopThreshold;

  const [scoreType, setScoreType] = useScoreType(
    props.scoreTypeOverride,
    props.defaultScoreType,
    props.onScoreTypeChange,
  );
  // TODO ML: Remove this
  const relativeMode = isRelative(scoreType);

  const initialShowValueChains = props.showValueChains || props.defaultShowValueChains || false;
  const [showValueChains, setShowValueChains] = useState(initialShowValueChains);

  const [modalImpactCode, setModalImpactCode] = useState<string | undefined>(undefined);

  useEffect(() => {
    setShowValueChains(!!props.showValueChains);
  }, [props.showValueChains]);

  const expandedStatesAndSetters = {
    [Dimension.ENVIRONMENT]: useState(!!props.expandedOnDefault?.find(d => d === Dimension.ENVIRONMENT)),
    [Dimension.HEALTH]: useState(!!props.expandedOnDefault?.find(d => d === Dimension.HEALTH)),
    [Dimension.KNOWLEDGE]: useState(!!props.expandedOnDefault?.find(d => d === Dimension.KNOWLEDGE)),
    [Dimension.SOCIAL]: useState(!!props.expandedOnDefault?.find(d => d === Dimension.SOCIAL)),
  };
  function setExpanded(dimension: Dimension, newExpanded: boolean) {
    const setter = expandedStatesAndSetters[dimension][1];
    setter(newExpanded);

    if (props.onExpandedChange) {
      props.onExpandedChange(dimension, newExpanded);
    }
  }
  function isExpanded(dimension: Dimension) {
    return expandedStatesAndSetters[dimension][0];
  }

  const absoluteModeDisabled = !allDataHasAbsolute(data);
  const relativeData = data?.map(d => d.impactScoresByTIVV?.[FinalImpactScoreType.RELATIVE]);
  const absoluteData = data?.map(d => d.impactScoresByTIVV?.[FinalImpactScoreType.ABSOLUTE]);
  const scaleMode = getScaleMode(relativeMode || absoluteModeDisabled, data?.length || 0);

  const scoreReaders = data?.map(d => new ScoreReader(d.impactScoresByTIVV, scoreType, d.revenue));
  const ghostScoreReaders = data?.map(d =>
    d.ghostScoresByTIVV ? new ScoreReader(d.ghostScoresByTIVV, scoreType, d.revenue) : undefined,
  );
  // Ghost baselines are always >= their series' own scores, so they must
  // participate in the x-scale or their tails would overflow the bar range.
  const allReaders = [
    ...(scoreReaders ?? []),
    ...(ghostScoreReaders ?? []).filter((r): r is ScoreReader => r !== undefined),
  ];
  const maxX = getMultiseriesMaxXFromScoreReaderFunctions(allReaders.map(s => s.readScore));

  const formatters = getImpactScoreFormattersFromScoreReaders(scoreReaders ?? []);
  const singleSeries = data?.length === 1 && !props.comparisonMode;

  // TODO OA: Figure out why the responsive wrapper gives varying widths as a prop for
  // some renders after loading data in the comparison component, and remove the hack below
  const previousWidth = usePrevious(props.width);
  const width = Math.abs(props.width - (previousWidth || 0)) < 20 ? previousWidth : props.width;

  const showRenderViewButton = props.showRenderViewButton !== undefined ? props.showRenderViewButton : true;

  const mainContext = useMainContext();

  const value: ImpactChartContext = {
    title: props.title,
    data: data || [],
    absoluteData: absoluteData || [],
    relativeData: relativeData || [],
    scoreReaders: scoreReaders ?? [],
    ghostScoreReaders: ghostScoreReaders ?? [],
    width: width || 0,
    desktopMode,
    bigFonts: props.bigFonts,
    maxX,
    scaleMode,
    scoreType,
    setScoreType,
    relativeMode,
    absoluteModeDisabled,
    showLegend: props.showLegend || false,
    scoreUnitSelectorPlacement: props.scoreUnitSelectorPlacement || 'left',
    showValueChains,
    setShowValueChains,
    hideValueChainsSwitch: !!props.hideValueChainsSwitch,
    showValueChainsSwitch: !!props.showValueChainsSwitch,
    modalImpactCode,
    setModalImpactCode,
    formatters,
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    netScoreFormat: props.netScoreFormat || mainContext.netScoreFormat || DEFAULT_RELATIVE_NET_SCORE_FORMAT,
    hideNetScore: props.hideNetScore,
    setExpanded,
    isExpanded,
    singleSeries,
    onImpactClick: props.onImpactClick,
    disableImpactClick: props.disableImpactClick,
    getImpactClickUrl: props.getImpactClickUrl,
    onContributeClick: props.onContributeClick,
    hideReleaseText: props.hideReleaseText === true,
    staticMode: !!props.staticMode,
    hideTitle: !!props.hideTitle,
    enableE2eTestIds: props.enableE2eTestIds,
    hasFinishedLoading: props.hasFinishedLoading || undefined,
    showRenderViewButton: showRenderViewButton,
    alignLineEdges: props.alignLineEdges,
    entityData: props.entityData,
    version: props.version,
    release: props.release,
    contributingArticlesCount: props.contributingArticlesCount,
    className: props.className,
    negativeColor: props.negativeColor,
    positiveColor: props.positiveColor,
    simplify: props.simplify,
    language: props.language,
    updateJobStatusRequest: props.updateJobStatusRequest,
    backgroundColor: props.backgroundColor,
  };

  return <Provider value={value}>{props.children}</Provider>;
}

export function useImpactChartContext() {
  return useContext(ctx);
}

export function useImpactChartWidths() {
  const { width, desktopMode, staticMode, simplify } = useImpactChartContext();
  return getWidths(width, desktopMode, staticMode, !!simplify);
}

export function useImpactChartHeights() {
  const { data } = useImpactChartContext();
  return getHeights(data.length);
}

export function useImpactChartActiveData(index: number) {
  const { relativeMode, data } = useImpactChartContext();
  return getNumberData({ relativeMode, data }, index);
}

export function useNetScore(index: number) {
  const netScoreFormat = useImpactChartContext().netScoreFormat;

  const scoreReaders = useImpactChartContext().scoreReaders;

  if (scoreReaders.length === 0) {
    return undefined;
  }

  const netScore = getScoreReaderTotal(scoreReaders[index], netScoreFormat);

  return netScore;
}

export function useImpactChartFontSizes(): ImpactChartFontSizes {
  const { bigFonts } = useImpactChartContext();
  const sizes = {
    columnTitles: bigFonts ? 16 : 12,
    impactTitles: bigFonts ? 22 : 16,
    dimensionTitles: bigFonts ? 22 : 16,
    dimensionNetScores: bigFonts ? 26 : 20, // TODO: different in compare mode different
    impactNetScores: bigFonts ? 18 : 14,
    impactScores: bigFonts ? 18 : 14,
    netScore: bigFonts ? 50 : 42,
    scoreTypeText: bigFonts ? 22 : 16,
    valueSetText: 14,
    modelVersionText: bigFonts ? 18 : 14,
    actionText: 14, // for suggest fixes
    inputsText: 14,
    legendText: 14,
  };

  // If we wish these to behave fluidly, we can
  // adapt the font sizes sizes here
  //
  // (We don't use regular media query based fluidiness for
  //  for the chart since the charts layout is driven by its
  //  container width and things get complicated if responsiviness
  //  is determined by two different variables, rather than one)
  //
  //   -- JO 14.9.2020
  const multiplier = 0.8;
  return mapValues(sizes, size => size * multiplier);
}

function isRelative(type: FinalImpactScoreType) {
  if (type === FinalImpactScoreType.RELATIVE) {
    return true;
  }
  if (type === FinalImpactScoreType.CENTS_PER_DOLLAR) {
    return true;
  }
  if (type === FinalImpactScoreType.DOLLARS_PER_YEAR) {
    return true; // ?
  }
  return false;
}

const useScoreType = (
  scoreTypeOverride: FinalImpactScoreType | undefined,
  defaultScoreType: FinalImpactScoreType | undefined,
  onScoreTypeChange: ((type: FinalImpactScoreType) => void) | undefined,
) => {
  const initialScoreType = scoreTypeOverride || defaultScoreType || defaultNetImpactLensScoreUnit;

  const [selectedScoreType, setSelectedScoreType] = useState<FinalImpactScoreType>(initialScoreType);

  useEffect(() => {
    if (scoreTypeOverride) {
      setSelectedScoreType(scoreTypeOverride);
    }
  }, [scoreTypeOverride]);

  const selectScoreType = (type: FinalImpactScoreType) => {
    setSelectedScoreType(type);
    if (onScoreTypeChange) {
      onScoreTypeChange(type);
    }
  };

  if (scoreTypeOverride) {
    return [scoreTypeOverride, undefined] as const;
  }

  return [selectedScoreType, selectScoreType] as const;
};
